import {format, isValid, parseISO} from 'date-fns';
import {ru} from 'date-fns/locale';
import {useMemo, useState} from 'react';
import type {DateRange} from 'react-day-picker';
import {resolvePeriod} from '@ozon-ai/periods';
import {Button} from '@/components/ui/button.tsx';
import {ButtonGroup} from '@/components/ui/button-group.tsx';
import {Calendar} from '@/components/ui/calendar.tsx';
import {ScrollArea} from '@/components/ui/scroll-area.tsx';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx';
import {cn} from 'cn';
import {formatPeriodRange, monthLabel} from './format-period';
import type {
	PeriodPickerCommitted,
	PeriodPickerState,
	PeriodPickerToggle,
} from './use-period-picker';

type PeriodPickerProps = PeriodPickerState & {
	dateFieldIdPrefix?: string;
};

type OpenMenu = 'month' | 'custom' | 'presets' | null;

const segmentButtonClassName = 'min-w-0 flex-1';

function parseBusinessDay(value: string): Date | undefined {
	if (!value) return undefined;
	const parsed = parseISO(value);
	return isValid(parsed) ? parsed : undefined;
}

function formatBusinessDay(date: Date): string {
	return format(date, 'yyyy-MM-dd');
}

function segmentVariant(active: boolean) {
	return active ? 'default' : 'outline';
}

function PeriodPickerLoaded({
	latestDay,
	months,
	presets,
	committed,
	resolvedLabel,
	localError,
	presetsButtonLabel,
	applyWeek,
	applyMonth,
	applyCustom,
	applyPreset,
}: Omit<PeriodPickerProps, 'committed' | 'ready'> & {committed: PeriodPickerCommitted}) {
	const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
	const [draftFrom, setDraftFrom] = useState(committed.customFrom);
	const [draftTo, setDraftTo] = useState(committed.customTo);

	const draftRange = useMemo((): DateRange | undefined => {
		const from = parseBusinessDay(draftFrom);
		const to = parseBusinessDay(draftTo);
		if (!from && !to) return undefined;
		return {from, to};
	}, [draftFrom, draftTo]);

	const draftInvalid =
		!draftFrom || !draftTo || draftFrom > draftTo
			? draftFrom && draftTo && draftFrom > draftTo
				? 'Дата «С» не может быть позже даты «По»'
				: 'Укажите обе даты'
			: null;

	const resetCustomDraft = () => {
		setDraftFrom(committed.customFrom);
		setDraftTo(committed.customTo);
	};

	const handleCustomOpenChange = (open: boolean) => {
		if (open) {
			setOpenMenu('custom');
			resetCustomDraft();
			return;
		}
		resetCustomDraft();
		setOpenMenu(null);
	};

	const handleApplyCustom = () => {
		if (draftInvalid || !draftFrom || !draftTo) return;
		applyCustom(draftFrom, draftTo);
		setOpenMenu(null);
	};

	const handleMonthValueChange = (yearMonth: string) => {
		if (!yearMonth) return;
		applyMonth(yearMonth);
		setOpenMenu(null);
	};

	const handlePresetSelect = (presetId: string) => {
		applyPreset(presetId);
		setOpenMenu(null);
	};

	return (
		<div className="flex w-full flex-col gap-2">
			<ButtonGroup className="w-full [&>[data-slot=button]]:min-w-0 [&>[data-slot=button]]:flex-1">
				<Button
					type="button"
					variant={segmentVariant(committed.toggle === 'week')}
					className={segmentButtonClassName}
					onClick={() => {
						setOpenMenu(null);
						applyWeek();
					}}
				>
					Неделя
				</Button>

				<DropdownMenu
					open={openMenu === 'month'}
					onOpenChange={(open) => setOpenMenu(open ? 'month' : null)}
				>
					<DropdownMenuTrigger
						render={
							<Button
								type="button"
								variant={segmentVariant(committed.toggle === 'month')}
								className={segmentButtonClassName}
							/>
						}
					>
						Месяц
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align="start"
						className="min-w-0 w-(--anchor-width) overflow-hidden p-0"
					>
						<ScrollArea className="h-60">
							<DropdownMenuRadioGroup
								value={committed.month}
								onValueChange={handleMonthValueChange}
								className="p-1.5 pr-3"
							>
								<DropdownMenuGroup>
									{months.map((item) => (
										<DropdownMenuRadioItem key={item} value={item}>
											{monthLabel(item)}
										</DropdownMenuRadioItem>
									))}
								</DropdownMenuGroup>
							</DropdownMenuRadioGroup>
						</ScrollArea>
					</DropdownMenuContent>
				</DropdownMenu>

				<DropdownMenu open={openMenu === 'custom'} onOpenChange={handleCustomOpenChange}>
					<DropdownMenuTrigger
						render={
							<Button
								type="button"
								variant={segmentVariant(committed.toggle === 'custom')}
								className={segmentButtonClassName}
							/>
						}
					>
						Период
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align="start"
						className="w-auto p-0"
						onClick={(event) => event.stopPropagation()}
					>
						<div className="flex flex-col gap-2 p-2">
							<Calendar
								mode="range"
								locale={ru}
								selected={draftRange}
								defaultMonth={
									parseBusinessDay(draftFrom) ?? parseBusinessDay(latestDay)
								}
								disabled={(date) => formatBusinessDay(date) > latestDay}
								onSelect={(range) => {
									setDraftFrom(range?.from ? formatBusinessDay(range.from) : '');
									setDraftTo(range?.to ? formatBusinessDay(range.to) : '');
								}}
							/>
							{draftInvalid ? (
								<p className="px-2 text-sm text-destructive">{draftInvalid}</p>
							) : null}
							<Button
								type="button"
								className="mx-2 mb-1 w-[calc(100%-1rem)]"
								disabled={Boolean(draftInvalid)}
								onClick={handleApplyCustom}
							>
								Применить
							</Button>
						</div>
					</DropdownMenuContent>
				</DropdownMenu>

				{presets.length > 0 ? (
					<DropdownMenu
						open={openMenu === 'presets'}
						onOpenChange={(open) => setOpenMenu(open ? 'presets' : null)}
					>
						<DropdownMenuTrigger
							render={
								<Button
									type="button"
									variant={segmentVariant(committed.toggle === 'presets')}
									className={cn(segmentButtonClassName, 'truncate')}
								/>
							}
						>
							<span className="truncate">{presetsButtonLabel}</span>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="end"
							className="min-w-0 w-(--anchor-width) max-w-none overflow-hidden p-0"
						>
							<ScrollArea className="max-h-60">
								<DropdownMenuGroup className="p-1.5 pr-3">
									{presets.map((preset) => {
									let preview = '';
									try {
										const range = resolvePeriod(
											{type: 'preset', preset: preset.definition},
											latestDay,
										);
										preview = formatPeriodRange(range.from, range.to);
									} catch {
										preview = '';
									}
									return (
										<DropdownMenuItem
											key={preset.id}
											className="flex flex-col items-start gap-0.5"
											onClick={() => handlePresetSelect(preset.id)}
										>
											<span>{preset.name}</span>
											{preview ? (
												<span className="text-xs font-normal text-muted-foreground">
													{preview}
												</span>
											) : null}
										</DropdownMenuItem>
									);
									})}
								</DropdownMenuGroup>
							</ScrollArea>
						</DropdownMenuContent>
					</DropdownMenu>
				) : null}
			</ButtonGroup>

			{localError ? <p className="text-sm text-destructive">{localError}</p> : null}

			{resolvedLabel ? (
				<p className="text-center text-sm text-muted-foreground">{resolvedLabel}</p>
			) : null}
		</div>
	);
}

export function PeriodPicker(props: PeriodPickerProps) {
	if (!props.ready || !props.committed) {
		return (
			<div className="flex w-full flex-col gap-2" aria-busy="true" aria-label="Загрузка периода">
				<div className="h-9 w-full animate-pulse rounded-4xl bg-muted" />
				<div className="mx-auto h-4 w-48 animate-pulse rounded bg-muted/80" />
			</div>
		);
	}
	return <PeriodPickerLoaded {...props} committed={props.committed} />;
}

export type {PeriodPickerToggle};
